import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { DataDisplayTable } from "components/data-display";
import { ShowHide } from "components/layout";
import { DateToLocal } from "components/dates";
import { useSelector } from 'react-redux';
import { TyreCard } from 'pages/tyre-checks/view/TyreCard';
import { Button } from "components/buttons";
import { Modal, ModalSize } from "components/notifications";
import { Tabs } from "components/navigation";
import 'pages/tyre-checks/TyreCheck.css';
import classNames from 'classnames';
import { Numeric } from 'components/formatting';

export function IndividualTyreCheck(props) {
    const [loading, setLoading] = useState(true);
    const tyreCheckData = useSelector(state => state.tyreCheck);
    const orderId = props.orderId;
    const order = tyreCheckData?.orders?.find(o => o.orderId === orderId);
    const columnsPerRow = 4;

    const [detailHeaderSections, setDetailHeaderSections] = useState([]);
    const [status, setStatus] = useState('');
    const [showAllCards, setShowAllCards] = useState(false);

    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [defectsRaised, setDefectsRaised] = useState(null);

    const Details = 0;
    const FullResults = 1;

    const tabSettingsDefault = [
        { title: 'Details', key: Details, selected: true },
        { title: 'Full Results', key: FullResults, selected: false }
    ];

    const [tabSettings, setTabSettings] = useState(tabSettingsDefault);


    const setUpDetailHeader = useCallback(() => {
        if (!tyreCheckData) return [];

        return [
            {
                header: "Vehicle Registration",
                value: order.registration,
                key: "vehicle_registration",
            },
            {
                header: "Vehicle Mileage",
                value: (
                    <Numeric>
                        {order.mileage}
                    </Numeric>
                ),
                key: "vehicle_mileage",
            },
            {
                header: "Auditor Name",
                value: tyreCheckData.auditorName,
                key: "auditor_name",
            },
            {
                header: "Check Location",
                value: tyreCheckData.locationName,
                key: "location_name",
            },
            {
                header: "Job Number",
                value: tyreCheckData.jobId,
                key: "job_number",
            },
            {
                header: "Start Date and Time",
                value: (
                    <DateToLocal format="DD/MM/YYYY HH:mm">
                        {order.orderStartTime}
                    </DateToLocal>
                ),
                key: "start-date",
            },
            {
                header: "Status",
                value: status,
                key: "status",
            },
            {
                header: "Defects Raised",
                value: defectsRaised,
                key: "defects-raised",
            }
        ];
    }, [tyreCheckData, status, order]);



    const setupPage = useCallback(() => {
        if (!order) return;
        setDetailHeaderSections(setUpDetailHeader());

        // Calculate new values
        const damages = order.positions?.flatMap(position =>
            (position.damages || [])
        ) || [];


        // Only update state if values have changed
        setDefectsRaised(prev => prev !== damages.length ? damages.length : prev);

        const hasDefects = Object.values(order.positions).some(position => Array.isArray(position.damages) && position.damages.length > 0);
        let allSignedOff = true;

        order.positions.forEach(position => {
            position.damages?.forEach(damage => {
                if (!damage.signedOffBy) {
                    allSignedOff = false;
                }
            });
        });

        if (hasDefects && allSignedOff) {
            setStatus('Signed-off');
        } else if (hasDefects && !allSignedOff) {
            setStatus('Fail');
        } else {
            setStatus('Pass');
        }
    }, [order, setUpDetailHeader]);

    useEffect(() => {
        if (tyreCheckData && tyreCheckData.orders) {
            setupPage();
            setLoading(false);
        }
    }, [setupPage, setLoading, tyreCheckData]);

    const tyreCards = Object.entries(order.positions)
        .filter(([positionId, positionData]) => {
            if (showAllCards) return true;
            return positionData.damages?.length > 0;
        })
        .map(([positionId]) => (
            <div className="TyreCard" key={positionId}>
                <TyreCard order={order} positionId={positionId} />
            </div>
        ));

    function onTabClick(tabIndex) {
        setTabSettings((prev) => {
            return prev.map(function (c, index) {
                return {
                    ...c,
                    selected: index === tabIndex
                }
            })
        }
        );
    }

    useEffect(() => {
        const detailsTab = tabSettings.find(tab => tab.title === 'Details');
        if (detailsTab && detailsTab.selected === false) {
            setShowAllCards(true);
        } else {
            setShowAllCards(false);
        }
    }, [tabSettings]);

    function generateImageUrl(img) {
        return `${img?.storageName}/${img.containerName}/${img?.image}?${img?.token}`;
    }

    const images = useMemo(() => {
        return (
            <div className="image-group" >
                {order.orderLevelImages.map((img) => (
                    <Button key={img.fileName}
                        onClick={() => {
                            setSelectedImage(generateImageUrl(img));
                            setShowImageModal(true);
                        }}
                        icon={<img src={generateImageUrl(img)} alt="" height={100} />}
                        minimal
                    />
                ))}
            </div>
        );
    }, [order.orderLevelImages]);


    return (
        <div>
            <ShowHide
                evaluator={!loading}
                show={
                    <div>
                        <div className="button-row">
                            <div className="spacer-bottom">
                                <Tabs tabs={tabSettings} onClick={onTabClick} loading={loading} />
                            </div>
                        </div>
                        <ShowHide evaluator={!showAllCards}
                            show={
                                <div>
                                    <div>
                                        <h3 className="tire-task-h3">Details</h3>
                                        <DataDisplayTable
                                            data={detailHeaderSections}
                                            displayInRows={false}
                                            loading={loading}
                                            columnsPerRow={columnsPerRow}
                                        />
                                    </div>
                                    <h3 className="tire-task-h3">Media</h3>
                                    <span>{order.orderLevelImages && order.orderLevelImages.length > 0 ? (
                                        <div>
                                            {images}
                                        </div>
                                    ) :
                                        <div className={classNames({ "bp6-skeleton": loading })}>
                                            No images.
                                        </div>
                                    }</span>
                                </div>
                            }
                        />

                        <div className={classNames('tyre-cards', { "bp6-skeleton": loading })}>
                            {tyreCards}
                        </div>

                    </div>
                }
            ></ShowHide>
            <Modal
                isOpen={showImageModal}
                onClose={() => setShowImageModal(false)}
                size={ModalSize.SMALL}
                showCloseButton={true}
            >
                <img alt="" src={selectedImage} style={{ width: '100%' }} />
            </Modal>
        </div>
    );
}